// ===========================================
// Report settings — Overview tab
// ===========================================
// Which of the nine report cards render on the Reports Overview page.
//
// ⚠️ The 4-tile quick-stats banner at the top of the Overview is deliberately
// NOT configurable (Andrew, 2026-09-16): it is the at-a-glance summary and has
// no per-tile route.

import { useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
  useReportsSettings,
  useUpdateReportsSettings,
  useResetReportsSettings,
} from '../../../hooks/useAdminSettings';
import {
  REPORT_KEYS,
  REPORT_LABELS,
  REPORT_PERIOD_SHAPE,
  type ReportKey,
} from '../../../constants/reportKeys.constants';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';

/** One boolean per report key — a checkbox set, not a partial patch. */
type FormData = Record<ReportKey, boolean>;

function allVisible(): FormData {
  return REPORT_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {} as FormData);
}

function toFormData(visibleCards: string[]): FormData {
  return REPORT_KEYS.reduce((acc, key) => {
    acc[key] = visibleCards.includes(key);
    return acc;
  }, {} as FormData);
}

export default function ReportsOverviewSettingsTab() {
  const { data: settings, isLoading, isError, error } = useReportsSettings();
  // The Retro Edits card is the owner's to show or hide (roles slice 2, V25): the server refuses
  // anyone else's save or reset that would change it, so the switch is locked for them and a
  // reset that would bring it back (it is in the defaults) is too.
  const { isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const resetWouldChangeRetroEdits = !!settings && !settings.visibleCards.includes('retro-edits');
  const updateMutation = useUpdateReportsSettings();
  const resetMutation = useResetReportsSettings();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<FormData>({ defaultValues: allVisible() });

  useEffect(() => {
    if (settings) {
      reset(toFormData(settings.visibleCards));
    }
  }, [settings, reset]);

  const current = watch();
  const selectedCount = REPORT_KEYS.filter((key) => current[key]).length;

  const onSubmit = (data: FormData) => {
    // Ordered by REPORT_KEYS so the persisted array is stable regardless of the
    // order the admin happened to toggle things in.
    const visibleCards = REPORT_KEYS.filter((key) => data[key]);
    updateMutation.mutate({ visibleCards: [...visibleCards] });
  };

  const handleReset = () => {
    if (window.confirm('Show all nine report cards on the Overview again? This cannot be undone.')) {
      resetMutation.mutate();
    }
  };

  // ⚠️ Three states, not two. An empty selection is a real, saved choice, so
  // "nothing ticked" must never be conflated with "still loading" or "failed".
  if (isLoading) {
    return (
      <Stack spacing={3}>
        <Skeleton variant="rectangular" height={120} />
        <Skeleton variant="rectangular" height={420} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Failed to load report settings{error instanceof Error ? `: ${error.message}` : '.'}
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardHeader
            title="Overview cards"
            subheader="Choose which report cards appear on the Reports Overview page. Hidden cards stay available from the Reports menu — and their data is no longer fetched, so the Overview loads faster."
          />
          <CardContent>
            {selectedCount === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No cards are selected. The Overview will show the quick-stats banner and an
                explanatory message instead of report cards.
              </Alert>
            )}

            <List disablePadding>
              {REPORT_KEYS.map((key, index) => (
                <Box key={key}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <Controller
                        name={key}
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            label=""
                            sx={{ mr: 0 }}
                            control={
                              <Switch
                                checked={field.value}
                                disabled={key === 'retro-edits' && !isOwner}
                                onChange={(e) => field.onChange(e.target.checked)}
                                inputProps={{
                                  'aria-label': `Show ${REPORT_LABELS[key]} on the Overview`,
                                }}
                              />
                            }
                          />
                        )}
                      />
                    }
                  >
                    <ListItemText
                      primary={REPORT_LABELS[key]}
                      secondary={
                        key === 'retro-edits' && !isOwner
                          ? `Only your ${ownerTitle} can show or hide this card`
                          : REPORT_PERIOD_SHAPE[key] === 'term'
                          ? 'Term-focused — shows the current term'
                          : REPORT_PERIOD_SHAPE[key] === 'snapshot'
                          ? 'A point-in-time snapshot'
                          : undefined
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {selectedCount} of {REPORT_KEYS.length} cards shown
            </Typography>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={resetMutation.isPending || (!isOwner && resetWouldChangeRetroEdits)}
          >
            Reset to Defaults
          </Button>
          <Button type="submit" variant="contained" disabled={!isDirty || updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
